import React from "react";
import { Box, Flex } from "theme-ui";

import Span from "components/typography/Span";
import { mnt } from "theme/maintenanceTokens";

/**
 * Shared Corrigo paste UI. Extracted so the Quote export panel
 * (Quotes/components/CorrigoExportPanel) and the Invoice submission panel
 * (Invoices/components/CorrigoInvoiceSubmissionPanel) render the SAME
 * line-card + copy-field treatment. Before this, each panel carried its
 * own `CorrigoField` and line-card markup and they had drifted: the quote
 * panel used the maintenance design system (bordered monospace value box +
 * attached dark Copy button that flips to a green check, active-line
 * highlight, always-green progress) while the invoice panel used raw hex +
 * a faint text-link Copy. Jack preferred the quote treatment, so it is the
 * one lifted here; both panels now import it and cannot diverge again.
 *
 * Design reference: mockup 08 (08-corrigo-paste-export.svg). Values come
 * from the mnt tokens, not inline hex.
 *
 * The invoice-only sections (WO Completion Details, Attachments, Cost
 * Reconciliation, Mark posted) stay in the invoice panel — this module
 * covers ONLY the shared line-by-line paste UI, which is identical on both
 * of CorrigoPro's Quote and Invoice forms (Category / Description / Qty /
 * Rate).
 */

/**
 * One copy target: a label, the value in a bordered monospace box, and an
 * attached Copy button on the right that flips to a green check once used.
 * `wide` spans the full line-card row (Category / Description); the default
 * is a half-width cell (Qty / Rate), matching mockup 08's grid.
 */
export function CorrigoField({
  label,
  value,
  onCopy,
  copied,
  wide,
}: {
  label: string;
  value: string | number;
  onCopy: () => void;
  copied?: boolean;
  wide?: boolean;
}) {
  return (
    <Box sx={{ flex: wide ? "1 1 100%" : "1 1 44%", minWidth: 0 }}>
      <Box
        sx={{
          fontFamily: "Poppins",
          fontSize: "10px",
          fontWeight: 600,
          letterSpacing: "0.3px",
          color: mnt.textMuted,
          marginBottom: "4px",
        }}
      >
        {label.toUpperCase()}
      </Box>
      <Flex
        sx={{
          alignItems: "stretch",
          border: `0.5px solid ${mnt.divider}`,
          borderRadius: "6px",
          overflow: "hidden",
          background: copied ? mnt.bgSubtle : mnt.cardBg,
          transition: "background 150ms ease",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            padding: "7px 10px",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: "12px",
            lineHeight: "18px",
            color: copied ? mnt.textMuted : mnt.ink,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={String(value)}
        >
          {value}
        </Box>
        <Box
          as="button"
          onClick={onCopy}
          aria-label={copied ? `Re-copy ${label} (already copied)` : `Copy ${label}`}
          sx={{
            flex: "0 0 auto",
            border: "none",
            borderLeft: `0.5px solid ${mnt.divider}`,
            padding: "0 14px",
            minWidth: "46px",
            background: copied ? mnt.greenPill : mnt.ink,
            color: copied ? mnt.green : mnt.cardBg,
            fontFamily: "Poppins",
            fontSize: "11px",
            fontWeight: 600,
            cursor: "pointer",
            transition: "background 150ms ease",
            "&:hover": { opacity: 0.9 },
          }}
        >
          {copied ? "\u2713" : "Copy"}
        </Box>
      </Flex>
    </Box>
  );
}

/**
 * One Corrigo line card: a header row (Line N + description, and a
 * completion status on the right) over the four copy fields. `active`
 * draws the dark border + shadow the mockup uses to mark the next line to
 * paste, so staff never lose their place in a strict-order paste.
 *
 * The four fields are passed as children so each panel keeps ownership of
 * its own copy handlers and copied-state keys; this component owns only
 * the shared card chrome + header. `last` drops the bottom margin.
 */
export function CorrigoLineCard({
  lineNumber,
  description,
  copiedCount,
  fieldsPerLine,
  active,
  last,
  children,
}: {
  lineNumber: number;
  description?: string;
  copiedCount: number;
  fieldsPerLine: number;
  active?: boolean;
  last?: boolean;
  children: React.ReactNode;
}) {
  const lineComplete = copiedCount === fieldsPerLine;
  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: active ? mnt.ink : mnt.divider,
        background: lineComplete ? mnt.bgSubtle : mnt.cardBg,
        borderRadius: "8px",
        padding: "13px 14px",
        marginBottom: last ? 0 : "10px",
        boxShadow: active ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
        transition:
          "background 150ms ease, border-color 150ms ease, box-shadow 150ms ease",
      }}
    >
      <Flex
        sx={{
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: "10px",
          gap: "12px",
        }}
      >
        <Flex sx={{ alignItems: "baseline", gap: "8px", minWidth: 0 }}>
          <Span
            fontFamily={"Poppins"}
            fontSize={"12px"}
            fontWeight={600}
            sx={{ color: mnt.ink, whiteSpace: "nowrap" }}
          >
            Line {lineNumber}
          </Span>
          {description && (
            <Span
              fontFamily={"Poppins"}
              fontSize={"12px"}
              sx={{
                color: mnt.textMuted,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {description}
            </Span>
          )}
        </Flex>
        <Span
          fontFamily={"Poppins"}
          fontSize={"11px"}
          fontWeight={600}
          sx={{
            color: lineComplete ? mnt.green : mnt.textMuted,
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {lineComplete ? "\u2713 Complete" : `${copiedCount} of ${fieldsPerLine} copied`}
        </Span>
      </Flex>
      <Flex sx={{ gap: "10px", flexWrap: "wrap" }}>{children}</Flex>
    </Box>
  );
}
